import type { CSSProperties } from 'react';

export const colors = {
  border: '#d8dee9',
  borderStrong: '#b8c2d1',
  text: '#142033',
  textMuted: '#526072',
  bg: '#ffffff',
  bgSubtle: '#f6f8fb',
  bgHover: '#eef4ff',
  blue: '#1565d8',
  blueSoft: '#e8f1ff',
  green: '#15803d',
  greenSoft: '#e9f8ef',
  graySoft: '#f3f4f6',
  red: '#dc2626',
  amberSoft: '#fff4de',
  violetSoft: '#f3ebff',
  redSoft: '#fdecec',
  shadow: '0 8px 24px rgba(20, 32, 51, 0.06)',
};

export const styles: Record<string, CSSProperties> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    color: colors.text,
    fontFamily: 'inherit',
  },
  card: {
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    boxShadow: colors.shadow,
  },

  filterCard: {
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    boxShadow: colors.shadow,
    overflow: 'hidden',
  },
  tableCard: {
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    boxShadow: colors.shadow,
    overflow: 'hidden',
  },
  saveNotification: {
    position: 'sticky',
    top: 10,
    zIndex: 25,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '10px 12px',
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
    boxShadow: '0 12px 30px rgba(20, 32, 51, 0.14)',
    fontSize: 13,
    fontWeight: 800,
  },
  saveNotificationSuccess: {
    background: '#ecfdf3',
    borderColor: '#86efac',
    color: '#166534',
  },
  saveNotificationError: {
    background: '#fef2f2',
    borderColor: '#fecaca',
    color: '#991b1b',
  },
  saveNotificationClose: {
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1,
  },
  buttonContent: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  spinner: {
    width: 14,
    height: 14,
    borderRadius: 999,
    border: '2px solid rgba(255, 255, 255, 0.5)',
    borderTopColor: '#ffffff',
    display: 'inline-block',
    animation: 'productTableEditorSpin 0.8s linear infinite',
  },
  disabledButton: {
    opacity: 0.65,
    cursor: 'not-allowed',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderBottom: `1px solid ${colors.border}`,
    flexWrap: 'wrap',
  },
  titleWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.15,
  },
  subtitle: {
    margin: 0,
    color: colors.textMuted,
    fontSize: 13,
  },
  summaryRow: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },
  statPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: colors.bgSubtle,
    border: `1px solid ${colors.border}`,
    borderRadius: 999,
    padding: '4px 8px',
    fontSize: 11,
    fontWeight: 700,
  },
  statPillMuted: {
    background: colors.graySoft,
    color: '#374151',
    borderColor: '#d1d5db',
  },
  toolbar: {
    display: 'grid',
    gridTemplateColumns: '1.4fr 1fr 1fr 0.8fr auto',
    gap: 8,
    padding: 12,
    borderBottom: `1px solid ${colors.border}`,
  },
  input: {
    width: '100%',
    minHeight: 36,
    borderRadius: 8,
    border: `1px solid ${colors.borderStrong}`,
    padding: '8px 10px',
    fontSize: 13,
    color: colors.text,
    background: colors.bg,
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    minHeight: 36,
    borderRadius: 8,
    border: `1px solid ${colors.borderStrong}`,
    padding: '8px 10px',
    fontSize: 13,
    color: colors.text,
    background: colors.bg,
    outline: 'none',
    boxSizing: 'border-box',
  },
  button: {
    minHeight: 36,
    borderRadius: 8,
    border: `1px solid ${colors.borderStrong}`,
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 700,
    background: colors.bg,
    color: colors.text,
    cursor: 'pointer',
  },
  legendWrap: {
    padding: '0 12px 10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  legendRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    border: `1px solid ${colors.border}`,
    background: colors.bgSubtle,
    cursor: 'pointer',
  },
  tableScroll: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    minWidth: 1260,
  },
  th: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: colors.bgSubtle,
    color: colors.text,
    textAlign: 'left',
    padding: '9px 8px',
    fontSize: 12,
    fontWeight: 800,
    borderBottom: `1px solid ${colors.borderStrong}`,
    whiteSpace: 'nowrap',
  },
  td: {
    verticalAlign: 'top',
    padding: 8,
    borderBottom: `1px solid ${colors.border}`,
    fontSize: 13,
    background: colors.bg,
  },
  deactivatedRowCell: {
    opacity: 0.48,
    filter: 'grayscale(0.38)',
  },
  cellButton: {
    display: 'block',
    width: '100%',
    padding: 0,
    margin: 0,
    border: 'none',
    background: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    color: colors.text,
  },
  editableBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: 4,
    borderRadius: 8,
    border: `1px solid transparent`,
  },
  textarea: {
    width: '100%',
    borderRadius: 8,
    border: `1px solid ${colors.blue}`,
    padding: '8px 10px',
    fontSize: 13,
    color: colors.text,
    background: colors.bg,
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  imgButton: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    alignItems: 'flex-start',
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
  },
  imgButtonInline: {
    display: 'inline-flex',
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  imgThumb: {
    width: 110,
    height: 110,
    objectFit: 'cover',
    borderRadius: 10,
    border: `1px solid ${colors.border}`,
    background: colors.bgSubtle,
  },
  imgThumbInline: {
    width: 72,
    height: 72,
    objectFit: 'cover',
    borderRadius: 8,
    border: `1px solid ${colors.border}`,
    background: colors.bgSubtle,
  },
  nameCellWrap: {
    display: 'grid',
    gridTemplateColumns: '72px 1fr',
    gap: 8,
    alignItems: 'start',
  },
  nameTextBlock: {
    display: 'grid',
    gap: 4,
  },
  dimensionGridCompact: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 2,
  },
  tinyText: {
    fontSize: 11,
    color: colors.textMuted,
  },
  tinyTextCompact: {
    fontSize: 10,
    color: colors.textMuted,
    marginTop: 4,
  },
  mono: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: 11,
  },
  tagWrap: {
    display: 'flex',
    gap: 4,
    flexWrap: 'wrap',
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    padding: '4px 7px',
    fontSize: 11,
    fontWeight: 700,
    background: colors.blueSoft,
    color: colors.text,
  },
  stackCompact: {
    display: 'grid',
    gap: 6,
  },
  inlineListItem: {
    display: 'grid',
    gridTemplateColumns: '1fr 110px 26px',
    gap: 4,
    alignItems: 'center',
  },
  miniInput: {
    width: '100%',
    minHeight: 30,
    borderRadius: 7,
    border: `1px solid ${colors.borderStrong}`,
    padding: '6px 8px',
    fontSize: 12,
    color: colors.text,
    background: colors.bg,
    outline: 'none',
    boxSizing: 'border-box',
  },
  priceInput: {
    textAlign: 'right',
    paddingLeft: 4,
    paddingRight: 4,
  },
  iconButton: {
    width: 26,
    height: 26,
    borderRadius: 7,
    border: `1px solid ${colors.borderStrong}`,
    background: colors.bg,
    color: colors.text,
    cursor: 'pointer',
    fontWeight: 800,
    lineHeight: 1,
  },
  iconButtonDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  stickyActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  stickyBar: {
    position: 'sticky',
    bottom: 0,
    zIndex: 5,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: 10,
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
    background: colors.bg,
    boxShadow: '0 10px 24px rgba(20, 32, 51, 0.1)',
    marginTop: 6,
    flexWrap: 'wrap',
  },
  bottomLegendRow: {
    display: 'flex',
    gap: 4,
    flexWrap: 'wrap',
    flex: 1,
  },
  bottomLegendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 6px',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 700,
    border: `1px solid ${colors.border}`,
  },
  bottomChangeSummary: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    flex: 1,
    minWidth: 220,
  },
  bottomChangePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '5px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
    border: `1px solid ${colors.border}`,
    whiteSpace: 'nowrap',
  },
  bottomChangePillEdited: {
    background: colors.blueSoft,
    color: '#174ea6',
    borderColor: '#bfdbfe',
  },
  bottomChangePillRemoved: {
    background: colors.graySoft,
    color: '#374151',
    borderColor: '#d1d5db',
  },
  bottomChangePillAdded: {
    background: colors.greenSoft,
    color: '#166534',
    borderColor: '#bbf7d0',
  },
  checkboxStack: {
    display: 'grid',
    gap: 4,
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    color: colors.text,
  },
  checkbox: {
    width: 14,
    height: 14,
    margin: 0,
    accentColor: colors.blue,
    cursor: 'pointer',
  },
  checkboxRed: {
    width: 14,
    height: 14,
    margin: 0,
    accentColor: '#c62828',
    cursor: 'pointer',
  },
  productActionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  rowDeactivateSwitch: {
    position: 'relative',
    width: 48,
    height: 24,
    borderRadius: 999,
    border: '2px solid #111827',
    background: '#ffffff',
    color: '#111827',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    padding: 0,
    boxSizing: 'border-box',
    transition: 'border-color 0.16s ease, background 0.16s ease, opacity 0.16s ease',
    flex: '0 0 auto',
  },
  rowDeactivateSwitchActive: {
    borderColor: '#b6beca',
    background: colors.greenSoft,
    color: '#111827',
  },
  rowDeactivateSwitchChecked: {
    borderColor: '#000000',
    background: '#ffffff',
    color: '#000000',
  },
  rowDeactivateSwitchInput: {
    position: 'absolute',
    inset: 0,
    opacity: 0,
    cursor: 'pointer',
    margin: 0,
  },
  rowDeactivateSwitchThumb: {
    position: 'absolute',
    top: 2,
    left: 2,
    width: 16,
    height: 16,
    borderRadius: 999,
    background: '#ffffff',
    border: '2px solid currentColor',
    boxSizing: 'border-box',
    transition: 'transform 0.16s ease',
    zIndex: 1,
  },
  rowDeactivateSwitchThumbActive: {
    transform: 'translateX(24px)',
  },
  rowDeactivateSwitchIcon: {
    position: 'absolute',
    right: 6,
    top: '50%',
    height: 13,
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    transition: 'opacity 0.16s ease',
  },
  rowDeactivateSwitchIconHidden: {
    opacity: 0,
  },
  deleteIconButton: {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: `1px solid #fecaca`,
    background: colors.redSoft,
    color: colors.red,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  flagFilterWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    padding: '0 12px 10px 12px',
  },
  flagPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: 999,
    border: `1px solid ${colors.border}`,
    background: colors.bgSubtle,
    fontSize: 11,
    fontWeight: 700,
  },
  countsFilterShell: {
    padding: '0 12px 10px 12px',
    display: 'grid',
    gap: 8,
  },
  countsFilterToggle: {
    minHeight: 36,
    borderRadius: 8,
    border: `1px solid ${colors.borderStrong}` ,
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 700,
    background: colors.bgSubtle,
    color: colors.text,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  countsFilterToggleMeta: {
    fontSize: 11,
    color: colors.textMuted,
  },
  countsFilterSection: {
    display: 'grid',
    gap: 10,
  },
  countsFilterGroup: {
    display: 'grid',
    gap: 6,
    padding: 8,
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    background: colors.bgSubtle,
  },
  countsFilterTitle: {
    fontSize: 12,
    fontWeight: 800,
    color: colors.text,
  },
  countsFilterGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  countFilterItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: 999,
    border: `1px solid ${colors.border}`,
    background: colors.bg,
    fontSize: 11,
    color: colors.text,
  },

  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: colors.bg,
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'stretch',
    padding: 0,
    zIndex: 50,
  },
  modalCard: {
    width: '100vw',
    height: '100dvh',
    maxHeight: 'none',
    overflow: 'hidden',
    background: colors.bg,
    borderRadius: 0,
    border: 'none',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    padding: '14px 16px 10px 16px',
    borderBottom: `1px solid ${colors.border}`,
  },
  modalTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 800,
    lineHeight: 1.1,
  },
  modalSubtitle: {
    margin: '4px 0 0 0',
    fontSize: 12,
    color: colors.textMuted,
  },
  modalStatsRow: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    padding: '10px 16px',
    borderBottom: `1px solid ${colors.border}`,
  },
  modalBody: {
    padding: 16,
    overflow: 'auto',
    display: 'grid',
    gap: 12,
  },
  modalSection: {
    display: 'grid',
    gap: 6,
  },
  modalSectionTitle: {
    fontSize: 13,
    fontWeight: 800,
  },
  modalList: {
    display: 'grid',
    gap: 6,
  },
  modalListItem: {
    padding: '8px 10px',
    borderRadius: 8,
    background: colors.bgSubtle,
    border: `1px solid ${colors.border}`,
    fontSize: 12,
  },
  modalListItemEdited: {
    background: colors.blueSoft,
    color: '#174ea6',
    borderColor: '#bfdbfe',
  },
  modalListItemRemoved: {
    background: colors.graySoft,
    color: '#374151',
    borderColor: '#d1d5db',
  },
  modalListItemAdded: {
    background: colors.greenSoft,
    color: '#166534',
    borderColor: '#bbf7d0',
  },
  emptyState: {
    padding: '16px 12px',
    borderRadius: 10,
    border: `1px dashed ${colors.borderStrong}`,
    background: colors.bgSubtle,
    fontSize: 13,
    color: colors.textMuted,
  },
  emptyStateSmall: {
    fontSize: 12,
    color: colors.textMuted,
  },
  savingState: {
    flex: 1,
    minHeight: 420,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 24,
    textAlign: 'center',
    background: colors.bg,
  },
  savingStateWithBackground: {
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    color: '#ffffff',
  },
  savingSpinner: {
    width: 46,
    height: 46,
    borderRadius: 999,
    border: `4px solid ${colors.blueSoft}`,
    borderTopColor: colors.blue,
    display: 'inline-block',
    animation: 'productTableEditorSpin 0.8s linear infinite',
  },
  savingSpinnerOnBackground: {
    borderColor: 'rgba(255, 255, 255, 0.34)',
    borderTopColor: '#ffffff',
    filter: 'drop-shadow(0 8px 20px rgba(0, 0, 0, 0.28))',
  },
  savingLogoWrap: {
    width: 252,
    height: 'auto',
    paddingBottom:'10px',
    borderRadius: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'productTableEditorPulse 1.4s ease-in-out infinite',
  },
  savingLogoImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    display: 'block',
  },
  savingTitle: {
    fontSize: 20,
    fontWeight: 900,
    color: colors.text,
  },
  savingTitleOnBackground: {
    color: '#ffffff',
    textShadow: '0 2px 16px rgba(0, 0, 0, 0.36)',
  },
  savingSubtitle: {
    maxWidth: 460,
    fontSize: 13,
    lineHeight: 1.45,
    color: colors.textMuted,
  },
  savingProgressText: {
    minHeight: 22,
    maxWidth: 520,
    fontSize: 15,
    lineHeight: 1.45,
    fontWeight: 800,
    color: colors.blue,
  },

  savingProgressBarShell: {
    width: 'min(420px, 100%)',
    height: 10,
    overflow: 'hidden',
    borderRadius: 999,
    background: colors.bgSubtle,
    border: `1px solid ${colors.border}`,
    boxShadow: 'inset 0 1px 2px rgba(20, 32, 51, 0.08)',
  },
  savingProgressBarShellOnBackground: {
    background: 'rgba(255, 255, 255, 0.24)',
    borderColor: 'rgba(255, 255, 255, 0.38)',
    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.2), 0 10px 26px rgba(0, 0, 0, 0.16)',
  },
  savingProgressBarFill: {
    height: '100%',
    minWidth: 8,
    borderRadius: 999,
    background: `linear-gradient(90deg, ${colors.blue}, #4f8df7)`,
    animation: 'productTableEditorPulse 1.2s ease-in-out infinite',
    transition: 'width 0.35s ease',
  },
  savingProgressBarFillOnBackground: {
    boxShadow: '0 0 18px rgba(255, 255, 255, 0.38)',
  },
  savingProgressMeta: {
    width: 'min(420px, 100%)',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    fontSize: 11,
    fontWeight: 800,
    color: colors.textMuted,
  },
  savingProgressMetaOnBackground: {
    color: 'rgba(255, 255, 255, 0.86)',
    textShadow: '0 1px 10px rgba(0, 0, 0, 0.3)',
  },
  savingElapsed: {
    marginTop: 4,
    padding: '7px 10px',
    borderRadius: 999,
    background: colors.bgSubtle,
    border: `1px solid ${colors.border}`,
    fontSize: 12,
    fontWeight: 800,
    color: colors.text,
  },
  savingElapsedOnBackground: {
    background: 'rgba(0, 0, 0, 0.32)',
    borderColor: 'rgba(255, 255, 255, 0.24)',
    color: '#ffffff',
    boxShadow: '0 10px 28px rgba(0, 0, 0, 0.16)',
  },


  saveResultState: {
    flex: 1,
    minHeight: 420,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 24,
    textAlign: 'center',
    background: colors.bg,
  },
  saveSuccessIcon: {
    color: '#15803d',
  },
  saveErrorIcon: {
    color: '#dc2626',
  },
  saveResultActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    padding: 0,
    marginTop: 8,
    borderTop: 'none',
    background: colors.bg,
  },

  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    padding: 16,
    borderTop: `1px solid ${colors.border}`,
    background: colors.bg,
  },
  modalSecondaryButton: {
    minHeight: 38,
    borderRadius: 8,
    border: `1px solid ${colors.borderStrong}`,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 700,
    background: colors.bg,
    color: colors.text,
    cursor: 'pointer',
  },
  resetChangesButton: {
    minHeight: 38,
    borderRadius: 8,
    border: `1px solid ${colors.borderStrong}`,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 800,
    background: colors.graySoft,
    color: '#374151',
    cursor: 'pointer',
  },
  addProductButton: {
    minHeight: 38,
    borderRadius: 8,
    border: `1px solid ${colors.green}`,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 800,
    background: colors.green,
    color: '#ffffff',
    cursor: 'pointer',
  },
  deleteConfirmButton: {
    minHeight: 38,
    borderRadius: 8,
    border: `1px solid ${colors.red}`,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 800,
    background: colors.red,
    color: '#ffffff',
    cursor: 'pointer',
  },

  saveButton: {
    minHeight: 38,
    borderRadius: 8,
    border: `1px solid ${colors.blue}`,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 800,
    background: colors.blue,
    color: '#ffffff',
    cursor: 'pointer',
  },
  formGridTwo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
  },
  formGridFour: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: 10,
  },
  formField: {
    display: 'grid',
    gap: 5,
    fontSize: 12,
    fontWeight: 800,
    color: colors.text,
  },
  modalForm: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    maxHeight: '88vh',
  },
  generatedPreview: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    padding: '8px 10px',
    borderRadius: 8,
    border: `1px dashed ${colors.borderStrong}`,
    background: colors.bgSubtle,
    fontSize: 12,
    color: colors.textMuted,
  },
  formGridThree: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 10,
  },
  formPriceRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 1fr) 86px 26px',
    gap: 6,
    alignItems: 'center',
  },
  formImageRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 1.4fr) minmax(160px, 1fr) 26px',
    gap: 6,
    alignItems: 'center',
  },
  imageDropZone: {
    display: 'grid',
    placeItems: 'center',
    gap: 4,
    minHeight: 110,
    borderRadius: 12,
    border: `1px dashed ${colors.borderStrong}`,
    background: colors.bgSubtle,
    cursor: 'pointer',
    padding: 14,
    textAlign: 'center',
  },
  imagePreviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 8,
  },
  imagePreviewCard: {
    display: 'grid',
    gridTemplateColumns: '64px 1fr 26px',
    gap: 8,
    alignItems: 'start',
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    background: colors.bg,
    padding: 8,
  },
  imagePreviewThumb: {
    width: 64,
    height: 64,
    borderRadius: 8,
    objectFit: 'cover',
    border: `1px solid ${colors.border}`,
    background: colors.bgSubtle,
  },
  imagePreviewMeta: {
    display: 'grid',
    gap: 5,
    minWidth: 0,
  },
  noResults: {
    padding: 24,
    textAlign: 'center',
    color: colors.textMuted,
  },
  confirmOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 70,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    background: 'rgba(20, 32, 51, 0.42)',
  },
  confirmDialog: {
    width: 'min(420px, 100%)',
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
    background: colors.bg,
    boxShadow: '0 20px 54px rgba(20, 32, 51, 0.24)',
    padding: 18,
    display: 'grid',
    gap: 10,
  },
  confirmIconWrap: {
    width: 46,
    height: 46,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: colors.redSoft,
    color: colors.red,
  },
  confirmTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1.15,
    color: colors.text,
  },
  confirmText: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.45,
    color: colors.textMuted,
  },
  confirmActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 6,
    flexWrap: 'wrap',
  },
};

export const legendSwatches = [
  colors.blueSoft,
  colors.greenSoft,
  colors.amberSoft,
  colors.violetSoft,
  colors.redSoft,
];

styles.addRowButton = {
  minHeight: 24,
  borderRadius: 6,
  border: `1px solid ${colors.borderStrong}`,
  padding: '3px 8px',
  fontSize: 11,
  fontWeight: 700,
  background: colors.bg,
  color: colors.text,
  cursor: 'pointer',
};

styles.addPriceButton = {
  minHeight: 24,
  borderRadius: 6,
  border: 'none',
  padding: '3px 8px',
  fontSize: 11,
  fontWeight: 700,
  background: 'rgb(237 242 243)',
  color: '#012877',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};
